import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    LuChevronDown,
    LuChevronUp,
    LuChevronLeft,
    LuX,
    LuUsers,
    LuLayoutDashboard,
    LuCircle,
    LuCircleDashed,
    LuShield,
    LuUserCheck,
    LuTrophy,
    LuMapPin,
    LuTags,
    LuFileText,
    LuBell,
    LuImage,
    LuMessageSquare,
    LuCreditCard,
    LuSettings,
    LuBookCopy,
    LuActivity,
    LuMonitorPlay,
} from 'react-icons/lu';

interface DropdownItem {
    title: string;
    href: string;
    old?: boolean;
}

interface SidebarItem {
    href?: string;
    title: string;
    icon: React.ReactNode;
    dropdownItems?: DropdownItem[];
    old?: boolean;
}

// A collapsible section grouping related items. `old` items carry an OLD badge
// (2025-era pages not yet re-audited); remove `old` from an item once its page
// has been verified/revamped so the badge disappears as we clean things up.
interface SidebarGroup {
    title: string;
    items: SidebarItem[];
}

interface SidebarProps {
    isCollapsed: boolean;
    isMobileOpen: boolean;
    setIsCollapsed: (value: boolean) => void;
    setIsMobileOpen: (value: boolean) => void;
}

const sidebarGroups: SidebarGroup[] = [
    {
        title: 'Core',
        items: [
            { title: 'Dashboard', icon: <LuLayoutDashboard className="w-5 h-5" />, href: '/', old: true },
            { title: 'Users', icon: <LuUsers className="w-5 h-5" />, href: '/users', old: true },
            { title: 'Matches', icon: <LuCircleDashed className="w-5 h-5" />, href: '/matches', old: true },
            { title: 'Tournaments', icon: <LuTrophy className="w-5 h-5" />, href: '/tournaments', old: true },
            { title: 'Teams', icon: <LuUserCheck className="w-5 h-5" />, href: '/teams', old: true },
        ],
    },
    {
        title: 'Cricket Ops',
        items: [
            { title: 'Match Ops', icon: <LuCircleDashed className="w-5 h-5" />, href: '/match-ops' },
            { title: 'Officials', icon: <LuUserCheck className="w-5 h-5" />, href: '/officials' },
            { title: 'Categories', icon: <LuTags className="w-5 h-5" />, href: '/categories', old: true },
            { title: 'Match Grounds', icon: <LuMapPin className="w-5 h-5" />, href: '/match-grounds' },
            { title: 'Ground Management', icon: <LuMapPin className="w-5 h-5" />, href: '/grounds', old: true },
            { title: 'SportMonks', icon: <LuActivity className="w-5 h-5" />, href: '/sportmonks' },
        ],
    },
    {
        title: 'Live Stream',
        items: [
            { title: 'Tickers', icon: <LuMonitorPlay className="w-5 h-5" />, href: '/tickers' },
        ],
    },
    {
        title: 'Moderation & Trust',
        items: [
            { title: 'Moderation', icon: <LuMessageSquare className="w-5 h-5" />, href: '/moderation' },
            { title: 'User 360', icon: <LuUsers className="w-5 h-5" />, href: '/user-360' },
            { title: 'Registrations', icon: <LuUserCheck className="w-5 h-5" />, href: '/registrations' },
            { title: 'Community Listings', icon: <LuMapPin className="w-5 h-5" />, href: '/community-listings' },
            { title: 'Ownership Claims', icon: <LuShield className="w-5 h-5" />, href: '/ownership-claims' },
            { title: 'Deleted Accounts', icon: <LuUsers className="w-5 h-5" />, href: '/deleted-accounts' },
            { title: 'Orphaned Entities', icon: <LuCircleDashed className="w-5 h-5" />, href: '/orphaned' },
        ],
    },
    {
        title: 'Platform',
        items: [
            { title: 'Homepage', icon: <LuLayoutDashboard className="w-5 h-5" />, href: '/homepage' },
            { title: 'Feature Flags', icon: <LuSettings className="w-5 h-5" />, href: '/feature-flags' },
            { title: 'Live Users', icon: <LuActivity className="w-5 h-5" />, href: '/live-users' },
            { title: 'Session Logs', icon: <LuFileText className="w-5 h-5" />, href: '/device-logs' },
            { title: 'App Links', icon: <LuFileText className="w-5 h-5" />, href: '/app-links' },
            {
                title: 'Notifications',
                icon: <LuBell className="w-5 h-5" />,
                dropdownItems: [
                    { title: 'Send Notification', href: '/send-notification', old: true },
                    { title: 'Broadcast Push', href: '/broadcast-notification' },
                    { title: 'Notification Templates', href: '/notification-templates', old: true },
                ],
            },
        ],
    },
    {
        title: 'Content & Config',
        items: [
            { title: 'Support', icon: <LuMessageSquare className="w-5 h-5" />, href: '/support' },
            { title: 'Pages', icon: <LuBookCopy className="w-5 h-5" />, href: '/pages', old: true },
            { title: 'Role & Permissions', icon: <LuShield className="w-5 h-5" />, href: '/roles', old: true },
        ],
    },
];

const isActive = (path: string) => {
    return window.location.pathname === path;
};

// Small amber pill marking a legacy (2025-era) page that hasn't been re-audited.
const OldBadge = () => (
    <span className="ml-2 text-[9px] font-semibold leading-none px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 tracking-wide flex-shrink-0">
        OLD
    </span>
);

const Sidebar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }: SidebarProps) => {
    // Item-level dropdown state (e.g. Notifications)
    const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: boolean }>({});

    // Group-level collapse state — every group starts expanded.
    const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>(() =>
        Object.fromEntries(sidebarGroups.map((g) => [g.title, true]))
    );

    // Toggle dropdown function
    const toggleDropdown = (itemTitle: string) => {
        setOpenDropdowns(prev => ({
            ...prev,
            [itemTitle]: !prev[itemTitle]
        }));
    };

    // Toggle a collapsible group section
    const toggleGroup = (groupTitle: string) => {
        setOpenGroups(prev => ({
            ...prev,
            [groupTitle]: prev[groupTitle] === false ? true : false
        }));
    };

    // Close mobile sidebar
    const closeMobileSidebar = () => {
        setIsMobileOpen(false);
    };

    // Toggle sidebar collapse
    const toggleSidebarCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    // Renders a single item (either a link or a nested dropdown). Shared between
    // the expanded (grouped) view and the collapsed (icons-only) view.
    const renderItem = (item: SidebarItem) => (
        <div key={item.title} className="relative">
            {item.dropdownItems ? (
                <>
                    {/* Dropdown toggle button */}
                    <button
                        onClick={() => !isCollapsed && toggleDropdown(item.title)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 text-gray-700 hover:text-primary-600 transition-colors group ${openDropdowns[item.title] ? 'bg-gray-100 text-primary-600' : ''
                            } ${isCollapsed ? 'justify-center' : ''}`}
                        aria-expanded={!isCollapsed && openDropdowns[item.title]}
                    >
                        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
                            <div className="text-primary-600 group-hover:text-primary-700 flex-shrink-0">
                                {item.icon}
                            </div>
                            {!isCollapsed && (
                                <span className="font-medium whitespace-nowrap">{item.title}</span>
                            )}
                        </div>
                        {!isCollapsed && (
                            <div className="flex-shrink-0">
                                {openDropdowns[item.title] ? (
                                    <LuChevronUp className="w-4 h-4" />
                                ) : (
                                    <LuChevronDown className="w-4 h-4" />
                                )}
                            </div>
                        )}
                    </button>

                    {/* Dropdown items */}
                    {!isCollapsed && openDropdowns[item.title] && (
                        <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                            {item.dropdownItems.map((dropdownItem) => (
                                <Link
                                    key={dropdownItem.href}
                                    to={dropdownItem.href}
                                    className={`flex items-center gap-3 p-2 rounded-lg transition-colors group ${isActive(dropdownItem.href)
                                        ? 'bg-primary-600 text-white'
                                        : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                                        }`}
                                    onClick={() => isMobileOpen && closeMobileSidebar()}
                                >
                                    <LuCircle className="text-xs flex-shrink-0" />
                                    <span className="whitespace-nowrap">{dropdownItem.title}</span>
                                    {dropdownItem.old && <OldBadge />}
                                </Link>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                /* Regular menu item */
                <Link
                    to={item.href as string}
                    className={`flex items-center p-3 rounded-lg transition-colors group ${isCollapsed ? 'justify-center' : 'space-x-3'
                        } ${isActive(item.href!)
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-primary-600'
                        }`}
                    onClick={() => isMobileOpen && closeMobileSidebar()}
                >
                    <span className={`flex-shrink-0 ${isActive(item.href!) ? 'text-white' : 'text-primary-600 group-hover:text-primary-700'
                        }`}>
                        {item.icon}
                    </span>
                    {!isCollapsed && (
                        <span className="font-medium whitespace-nowrap">{item.title}</span>
                    )}
                    {!isCollapsed && item.old && <OldBadge />}
                </Link>
            )}
        </div>
    );

    return (
        <>
            {/* Mobile overlay */}
            {isMobileOpen && (
                <div
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 opacity-100 z-40 lg:hidden"
                    onClick={closeMobileSidebar}
                />
            )}

            <aside
                id="sidebar"
                className={`fixed top-0 left-0 h-full ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'
                    } ${isCollapsed ? 'w-20' : 'w-72'
                    } lg:translate-x-0 transition-all duration-300 ease-in-out bg-white border-r border-gray-200 flex flex-col shadow-lg z-40`}
            >
                {/* Header */}
                <div className={`h-16 flex items-center justify-between border-b border-gray-200 bg-white ${isCollapsed ? 'px-2' : 'px-4'}`}>
                    <div className={`flex items-center gap-3 overflow-hidden ${isCollapsed ? 'justify-center' : ''}`}>
                        <img
                            src="/assets/images/small-logo.png"
                            alt="Logo"
                            className='w-10 h-10 object-contain flex-shrink-0'
                        />
                        {!isCollapsed && (
                            <span className="font-semibold text-gray-800 text-lg whitespace-nowrap">
                                CrikStars
                            </span>
                        )}
                    </div>

                    {/* Mobile close button */}
                    {isMobileOpen && (
                        <button
                            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            onClick={closeMobileSidebar}
                            aria-label="Close sidebar"
                        >
                            <LuX className="w-5 h-5 text-gray-600" />
                        </button>
                    )}

                    {/* Desktop collapse button */}
                    <button
                        className="hidden lg:flex p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={toggleSidebarCollapse}
                        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        <LuChevronLeft className={`w-5 h-5 text-gray-600 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
                    {isCollapsed
                        ? /* Collapsed: flat icon list, no group headers or badges */
                        sidebarGroups.flatMap((group) => group.items).map((item) => renderItem(item))
                        : /* Expanded: collapsible group sections */
                        sidebarGroups.map((group) => (
                            <div key={group.title} className="pt-2">
                                <button
                                    onClick={() => toggleGroup(group.title)}
                                    className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors"
                                    aria-expanded={openGroups[group.title] !== false}
                                >
                                    <span className="whitespace-nowrap">{group.title}</span>
                                    {openGroups[group.title] !== false ? (
                                        <LuChevronUp className="w-3.5 h-3.5" />
                                    ) : (
                                        <LuChevronDown className="w-3.5 h-3.5" />
                                    )}
                                </button>
                                {openGroups[group.title] !== false && (
                                    <div className="mt-1 space-y-1">
                                        {group.items.map((item) => renderItem(item))}
                                    </div>
                                )}
                            </div>
                        ))}
                </nav>
            </aside>
        </>
    );
};

export default Sidebar;
